import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'

// 懒加载组件
const Login = () => import('@/views/Login.vue')
const Layout = () => import('@/layout/Layout.vue')
const Dashboard = () => import('@/views/dashboard/Dashboard.vue')
const Organization = () => import('@/views/organization/Organization.vue')
const ResidentInfo = () => import('@/views/resident/ResidentInfo.vue')
const PaymentList = () => import('@/views/payment/PaymentList.vue')
const PaymentTask = () => import('@/views/task/PaymentTask.vue')
const Statistics = () => import('@/views/statistics/Statistics.vue')
const ExportAudit = () => import('@/views/audit/ExportAudit.vue')
const UserCenter = () => import('@/views/user/UserCenter.vue')
const UserManage = () => import('@/views/system/UserManage.vue')
const OperationLog = () => import('@/views/system/OperationLog.vue')
const LoginLog = () => import('@/views/system/LoginLog.vue')
const NotFound = () => import('@/views/error/NotFound.vue')
const Forbidden = () => import('@/views/error/Forbidden.vue')

const routes = [
  {
    path: '/',
    redirect: '/sso'
  },
  {
    path: '/sso',
    name: 'SSO',
    component: Login,
    meta: { title: '一表通认证', requiresAuth: false }
  },
  {
    // 兼容习惯性输入 /login，统一指向 SSO 登录页
    path: '/login',
    redirect: '/sso'
  },
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/dashboard/index',
    meta: { title: '首页', requiresAuth: true },
    children: [
      {
        path: 'index',
        name: 'Dashboard',
        component: Dashboard,
        meta: { title: '仪表盘', icon: 'dashboard' }
      }
    ]
  },
  {
    path: '/organization',
    component: Layout,
    redirect: '/organization/list',
    // 与后端组织架构接口 @PreAuthorize(CITY_ADMIN/COUNTY_ADMIN) 及 /auth/menus 保持一致
    meta: { title: '组织架构', requiresAuth: true, roles: ['CITY_ADMIN', 'COUNTY_ADMIN'] },
    children: [
      {
        path: 'list',
        name: 'Organization',
        component: Organization,
        meta: { title: '组织管理', icon: 'office-building', roles: ['CITY_ADMIN', 'COUNTY_ADMIN'] }
      }
    ]
  },
  {
    path: '/resident',
    component: Layout,
    redirect: '/resident/list',
    meta: { title: '居民管理', requiresAuth: true },
    children: [
      {
        path: 'list',
        name: 'ResidentInfo',
        component: ResidentInfo,
        meta: { title: '居民信息', icon: 'user' }
      }
    ]
  },
  {
    path: '/payment',
    component: Layout,
    redirect: '/payment/list',
    meta: { title: '缴纳管理', requiresAuth: true },
    children: [
      {
        path: 'list',
        name: 'PaymentList',
        component: PaymentList,
        meta: { title: '缴纳清单', icon: 'document' }
      },
      {
        path: 'task',
        name: 'PaymentTask',
        component: PaymentTask,
        meta: { title: '缴费任务清单', icon: 'list' }
      }
    ]
  },
  {
    path: '/statistics',
    component: Layout,
    redirect: '/statistics/index',
    meta: { title: '统计分析', requiresAuth: true },
    children: [
      {
        path: 'index',
        name: 'Statistics',
        component: Statistics,
        meta: { title: '数据统计', icon: 'trend-charts' }
      }
    ]
  },
  {
    path: '/audit',
    component: Layout,
    redirect: '/audit/export',
    // 与后端导出审核角色白名单一致（ExportRecordServiceImpl.REVIEW_ROLES）
    meta: { title: '导出管理', requiresAuth: true, roles: ['ADMIN', 'CITY_ADMIN', 'COUNTY_ADMIN', 'TOWN_ADMIN', 'TOWN_WORKER'] },
    children: [
      {
        path: 'export',
        name: 'ExportAudit',
        component: ExportAudit,
        meta: { title: '导出审核', icon: 'document-checked', roles: ['ADMIN', 'CITY_ADMIN', 'COUNTY_ADMIN', 'TOWN_ADMIN', 'TOWN_WORKER'] }
      }
    ]
  },
  {
    path: '/user',
    component: Layout,
    redirect: '/user/center',
    meta: { title: '个人中心', requiresAuth: true },
    children: [
      {
        path: 'center',
        name: 'UserCenter',
        component: UserCenter,
        meta: { title: '个人设置', icon: 'setting' }
      }
    ]
  },
  {
    path: '/system',
    component: Layout,
    redirect: '/system/user',
    meta: { title: '系统管理', requiresAuth: true, roles: ['CITY_ADMIN'] },
    children: [
      {
        path: 'user',
        name: 'SystemUser',
        component: UserManage,
        meta: { title: '用户管理', icon: 'user', roles: ['CITY_ADMIN'] }
      },
      {
        path: 'oplog',
        name: 'SystemOperationLog',
        component: OperationLog,
        meta: { title: '操作日志', icon: 'document', roles: ['CITY_ADMIN'] }
      },
      {
        path: 'loginlog',
        name: 'SystemLoginLog',
        component: LoginLog,
        meta: { title: '登录日志', icon: 'key', roles: ['CITY_ADMIN'] }
      }
    ]
  },
  {
    path: '/403',
    name: 'Forbidden',
    component: Forbidden,
    meta: { title: '403', requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { title: '404', requiresAuth: false }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 路由守卫
router.beforeEach((to, from, next) => {
  const token = store.state.user.token
  
  // 设置页面标题
  if (to.meta.title) {
    document.title = `${to.meta.title} - 医保缴纳服务系统`
  }
  
  // SSO中转页不需要认证检查
  if (to.path === '/sso') {
    next()
    return
  }
  
  // 检查是否需要认证
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!token) {
      // 未登录，跳转到SSO中转页（由中转页负责跳转一表通）
      next({ path: '/sso', query: { redirect: to.fullPath } })
    } else {
      // 检查用户权限
      const userRole = store.state.user.role
      if (to.meta.roles && to.meta.roles.length > 0) {
        if (to.meta.roles.includes(userRole)) {
          next()
        } else {
          next({ path: '/403' })
        }
      } else {
        next()
      }
    }
  } else {
    next()
  }
})

export default router